import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  limit,
  onSnapshot,
  FirestoreDataConverter,
  Unsubscribe,
} from 'firebase/firestore';
import type { Rating } from '@segmently/schema-models/entities';
import { CoreFirebaseService } from './CoreFirebaseService';
import { ratingConverter as clientRatingConverter } from '../converters/ratingConverter';

/**
 * RatingService
 *
 * Type-safe service for Rating entity CRUD operations.
 * Ratings are stored as subcollection under projects.
 *
 * Path: projects/{projectId}/ratings/{ratingId}
 *
 * Features:
 * - Basic CRUD operations (create, read, delete)
 * - Type-safe: only Rating types exposed, no Firebase types in public API
 * - Uses ratingConverter for Date↔Timestamp conversion
 *
 * Usage:
 * ```typescript
 * import { RatingService } from '@/modules/firebase-core/services/RatingService';
 *
 * const service = new RatingService('project-id');
 *
 * // Create
 * const rating = await service.create({
 *   rating: '4.8',
 *   ratingCount: '10K',
 *   platform: 'ios',
 *   description: 'App Store rating'
 * });
 *
 * // Read
 * const found = await service.get(rating.id!);
 *
 * // Delete
 * await service.delete(rating.id!);
 * ```
 */
export class RatingService {
  private readonly collectionName = 'ratings';
  private readonly firestore = CoreFirebaseService.getInstance().firestore;
  private readonly converter: FirestoreDataConverter<Rating>;
  private readonly projectId: string;

  /**
   * Create a new RatingService instance
   *
   * @param projectId - Parent project ID
   * @param converter - Firestore converter for Rating entity (defaults to client SDK converter)
   */
  constructor(projectId: string, converter: FirestoreDataConverter<Rating> = clientRatingConverter) {
    this.projectId = projectId;
    this.converter = converter;
  }

  /**
   * Create a new rating
   *
   * @param data - Partial rating data (id will be auto-generated if not provided)
   * @returns {Promise<Rating>} The created rating with generated ID
   *
   * @example
   * ```typescript
   * const rating = await service.create({
   *   rating: '4.5',
   *   ratingCount: '5K',
   *   platform: 'android',
   *   description: 'Google Play rating'
   * });
   * ```
   */
  async create(data: Partial<Rating>): Promise<Rating> {
    try {
      const collectionRef = collection(this.firestore, 'projects', this.projectId, this.collectionName);
      const docRef = doc(collectionRef).withConverter(this.converter);

      const ratingData: Rating = {
        ...data,
        id: docRef.id,
        createdAt: new Date(),
      } as Rating;

      await setDoc(docRef, ratingData);

      return ratingData;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'RatingService.create');
      throw error;
    }
  }

  /**
   * Get a rating by ID
   *
   * @param id - Rating ID
   * @returns {Promise<Rating | null>} The rating or null if not found
   *
   * @example
   * ```typescript
   * const rating = await service.get('rating-123');
   * if (rating) {
   *   console.log(rating.rating);
   * }
   * ```
   */
  async get(id: string): Promise<Rating | null> {
    try {
      const docRef = doc(this.firestore, 'projects', this.projectId, this.collectionName, id)
        .withConverter(this.converter);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data();
      return { ...data, id: docSnap.id } as Rating;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'RatingService.get');
      throw error;
    }
  }

  /**
   * Delete a rating
   *
   * @param id - Rating ID
   * @returns {Promise<void>}
   *
   * @example
   * ```typescript
   * await service.delete('rating-123');
   * ```
   */
  async delete(id: string): Promise<void> {
    try {
      const docRef = doc(this.firestore, 'projects', this.projectId, this.collectionName, id);
      await deleteDoc(docRef);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'RatingService.delete');
      throw error;
    }
  }

  /**
   * Update a rating
   *
   * @param id - Rating ID
   * @param data - Partial rating data to merge
   * @returns {Promise<void>}
   */
  async update(id: string, data: Partial<Rating>): Promise<void> {
    try {
      const docRef = doc(this.firestore, 'projects', this.projectId, this.collectionName, id);
      await updateDoc(docRef, data as Record<string, unknown>);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'RatingService.update');
      throw error;
    }
  }

  /**
   * Subscribe to real-time rating updates
   *
   * @param onData - Callback with updated ratings array
   * @param onError - Optional error callback
   * @returns {Unsubscribe} Unsubscribe function
   */
  subscribe(
    onData: (items: Rating[]) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    const collectionRef = collection(this.firestore, 'projects', this.projectId, this.collectionName)
      .withConverter(this.converter);
    const q = query(collectionRef, orderBy('createdAt', 'desc'));
    return onSnapshot(q, (snap) => {
      onData(snap.docs.map(d => ({ ...d.data(), id: d.id } as Rating)));
    }, onError);
  }

  /**
   * List ratings for the project
   *
   * @param options - Query options
   * @param options.limit - Maximum number of ratings to return (default: 20)
   * @returns {Promise<Rating[]>} Array of ratings
   *
   * @example
   * ```typescript
   * const ratings = await service.list({ limit: 50 });
   * ```
   */
  async list(options: { limit?: number } = {}): Promise<Rating[]> {
    try {
      const collectionRef = collection(this.firestore, 'projects', this.projectId, this.collectionName)
        .withConverter(this.converter);

      const pageLimit = options.limit || 20;
      const q = query(collectionRef, orderBy('createdAt', 'desc'), limit(pageLimit));

      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      } as Rating));
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'RatingService.list');
      throw error;
    }
  }
}
